/**
 * The per-row fetch plan behind `CellAccessor.rowHandle`: what one row's
 * cache fetches for a range, by who owns the row's data (a layout row has
 * none; a client-side provider computes them; an alias reads its batch key
 * after the sheet's bulk read; a lookup-keyed row reads by key unless the
 * bulk read delivered the range; a canonical key with no row metadata is
 * fetched by the key and flagged loudly; a user row reads by id). Each
 * settle that delivered cells is announced to the rows it renders as, one
 * row-scoped `ColumnDataChanged` per row.
 *
 * The plan reads the sheet through a structural port (`RowFetchPlan.Port`):
 * the accessor resolves a row's data owner from its descriptor and hands the
 * plan the result (`RowFetchPlan.Owner`), so this module names no row type.
 *
 * @module
 */

import type { Backend, CellEntry, Option, RowLocalId } from "@tento-lona/sheets";
import { DateTime, NaiveDate, SemanticTime, Utc } from "@tento-chrono";
import { nonnull } from "@tento-core/assert";
import { Tracing } from "@tento-core/tracing";
import { RowId, RowNetworkKey } from "@tento-lona/sheets";
import { Network } from "../network.ts";
import { type Actor, ColumnDataChanged, type InvalidationSink } from "../invalidation.ts";
import { fetchedAsWireCells, type RowCellCache } from "../sync/row-cell-cache.ts";
import { UtcDays } from "@tento-lona/sheets/internal-sdk";

export class RowFetchPlan {
  readonly #port: RowFetchPlan.Port;
  /** Rows already flagged for a fetch-registry gap (warn once per row). */
  readonly #warnedRegistryGaps = new Set<string>();

  constructor(port: RowFetchPlan.Port) {
    this.#port = port;
  }

  /**
   * The fetch `rh` (the cache of `rowId`) runs for a range. `ownFetch` is the
   * backend's own per-row fetch for the row (`rh.fetchBindings.own`, never
   * another accessor's binding: a closure over another sheet would keep it alive).
   */
  fetchFn(
    rowId: RowNetworkKey,
    rh: RowCellCache,
  ): (range: DateTime.Range<Utc>) => Promise<Network.SheetCell[]> {
    const port = this.#port;
    const originalFetch = rh.fetchBindings.own;

    return (range) => {
      const owner = port.ownerOf(rowId);

      if (owner != null) {
        // Layout rows have no cell data
        if (owner.kind === "layout") return Promise.resolve([]);

        // Client-side provider: inject computeRange once, then return empty.
        // RowCellCache.ensureFresh will call computeRange instead of fetching.
        if (owner.kind === "provider") {
          if (!rh.computeRange) {
            const compute = owner.computeCells;
            rh.setComputeRange((naiveRange) => Promise.resolve(compute(naiveRange)));
            rh.kind = "formula";
          }
          return Promise.resolve([]);
        }

        // Alias resolver: renderer rows project from source rows; they do not
        // become canonical owners of the underlying cell/index state. Await the
        // bulk sheet fetch first, then warm projected child/source rows. Until
        // the meta-row shim exists, renderer rows may still need their own
        // compat batch-key fetch so row-local indices used by existing plugins
        // continue to populate.
        if (owner.kind === "alias") {
          const batchKey = owner.batchKey;
          const sheetFetch = rh.sheetFetch;
          if (sheetFetch) {
            return (async () => {
              await owner.ensureEncoding(rh);
              await sheetFetch(new NaiveDate.Range(
                NaiveDate.fromDse(range.start.dse),
                NaiveDate.fromDse(range.end.dse),
              ));
              // Bulk fetch delivered data via receiveBatchData — check for actual cells
              if (rh.allCells().length > 0) return [];
              const childRowIds = port.childrenOf(rowId);
              if (childRowIds.length > 0) {
                for (const childRowId of childRowIds) {
                  await port.rowHandle(childRowId).fetch(range);
                }
              }
              return this.#readByKey(batchKey, range);
            })();
          }
          return (async () => {
            await owner.ensureEncoding(rh);
            return this.#readByKey(batchKey, range);
          })();
        }
      }

      // Rows with lookup keys: await bulk fetch first if in a sheet context.
      const lookupKey = owner?.kind === "plain" ? owner.lookupKey : null;
      if (lookupKey) {
        const sheetFetch2 = rh.sheetFetch;
        if (sheetFetch2) {
          return (async () => {
            await sheetFetch2(new NaiveDate.Range(
              NaiveDate.fromDse(range.start.dse),
              NaiveDate.fromDse(range.end.dse),
            ));
            // Bulk delivery goes through `receiveBatchData`, which
            // extends `rh.deliveredRange` — that is the delivery
            // signal. Cell PRESENCE is not: bridged local mirrors
            // (source-store → handle) pre-populate cells without any
            // fetch, and treating those as delivery would suppress
            // the per-row fetch that carries the datetime-precise
            // cells. `fetcher.fetchedRange` is no good either: it is
            // extended optimistically at fetch START, so it always
            // covers the in-flight request.
            const delivered = rh.deliveredRange;
            if (
              delivered != null &&
              delivered.start.mse <= range.start.mse &&
              delivered.end.mse >= range.end.mse
            ) {
              return [];
            }
            const fetched = await this.#readByKey(
              nonnull(lookupKey, "CellAccessor.fetchFn: lookup-keyed row"),
              range,
            );
            // Relocation-repaint diagnostics: the async settle of a
            // per-row fetch — pair with `weather/bind-data` to see
            // whether a bound cell observed this settle.
            Tracing.log("sheet/fetch-settle", {
              rowKey: lookupKey,
              cells: fetched?.length ?? 0,
              via: "lookup-key",
            });
            this.#scheduleSettleInvalidation(rowId, rh, fetched);
            return fetched;
          })();
        }
        return this.#readByKey(lookupKey, range);
      }

      // Floor: the id is itself a canonical shared key (`:~weather:{lid}`,
      // `:~lona:tasks`) but the row's metadata was absent, so the lookupKey
      // branch was skipped — the key is still server-addressable, so fetch by
      // it directly rather than silently returning empty (which self-caches as
      // "done" and blanks the row forever). A meta-less canonical row here IS
      // the row-metadata two-registries gap (deps.rows missing a row the
      // renderer can see), so flag it LOUDLY: that conversion of silent-empty
      // into a named signal is what would have caught this in hours, not days
      // (see `2026-06-19-ephemeral-canonical-key-fetch-backstop`). The
      // backstop + signal stay until the authority collapses deps.rows onto
      // rowsSheet (row-metadata-authority P1.6 Phase 2), which removes the gap
      // at the source.
      const parsed = RowId.of(rowId);
      if (parsed.isReserved() || parsed.isLookup()) {
        if (owner == null) {
          this.#warnRegistryGap(rowId, "self-addressed-canonical-key");
        }
        return this.#readByKey(rowId, range).then((fetched) => {
          // Relocation-repaint diagnostics: settle of the canonical-key
          // backstop fetch (see `warnRegistryGap` above) — pair with
          // `weather/bind-data`.
          Tracing.log("sheet/fetch-settle", {
            rowKey: rowId,
            cells: fetched?.length ?? 0,
            via: "canonical-backstop",
          });
          this.#scheduleSettleInvalidation(rowId, rh, fetched);
          return fetched;
        });
      }

      // Only UUID-backed rows can be fetched by ID — everything else
      // (virtual, template, pending, system) relies on bulk fetch
      if (!parsed.isUser()) return Promise.resolve([]);

      return originalFetch(range);
    };
  }

  /**
   * One row's cells by key over `range`, read through the sheets `Backend`
   * (`fetchCellsByKey`) for the days `range` spans (`range.start.date` ..
   * `range.end.date`, the days the request names), answered in the wire
   * shape `RowCellCache` ingests (`fetchedAsWireCells`). The `Backend`
   * does not decode cell values; the cache does, once.
   */
  async #readByKey(key: string, range: DateTime.Range<Utc>): Promise<Network.SheetCell[]> {
    // The days the range touches (09:00-17:00 on D reads D..D+1).
    const fetched = await this.#port.sheets.fetchCellsByKey(RowNetworkKey.fromWire(key), UtcDays.touched(range));
    return fetchedAsWireCells(fetched);
  }

  /**
   * Loud-on-gap signal: the per-row fetch had to resolve a row outside
   * `deps.rows` (via the rowsSheet authority, or by self-addressing a
   * canonical key) because deps.rows was missing a row the renderer can
   * see. That asymmetry is the row-metadata-authority two-registries split
   * (P1.6); it's benign here because we recovered, but the SAME shape with
   * no recoverable fetch key is how blank-cell bugs hide. Surface it once
   * per row so the next divergence is a named signal, not silent-empty.
   */
  #warnRegistryGap(rowId: RowNetworkKey, how: string): void {
    if (this.#warnedRegistryGaps.has(rowId)) return;
    this.#warnedRegistryGaps.add(rowId);
    Tracing.warn("net")(
      `fetch-registry gap: row ${rowId} is absent from the fetch registry ` +
        `(deps.rows) but visible to the renderer; recovered via ${how}. This ` +
        `is the row-metadata-authority two-registries split (P1.6) — the row ` +
        `should be registered in the fetch path's registry.`,
    );
  }

  /**
   * Announce a per-row fetch's settle to the rows it renders as (one scoped
   * `rows.<id>.cells.<col>` → a host's scoped `CellContentColumn` effect)
   * per settle that delivered cells, through the handle's post-apply hook so
   * every sink subscriber observes the SETTLED cells. NEVER a sheet-wide
   * event here — that would feed the structural storm
   * (relocation-does-not-flash-unrelated-cells).
   */
  #scheduleSettleInvalidation(
    rowId: RowNetworkKey,
    rh: RowCellCache,
    fetched: Option<readonly Network.SheetCell[]>,
  ): void {
    const sink = this.#port.invalidation;
    if (!sink || !fetched || fetched.length === 0) return;
    // A row whose cache reads through the sheet's cell store: the answer
    // landed in the store, which announced every cell it changed (the
    // rows-side `ColumnDataChanged`, scoped to the row) — a settle event too
    // would announce those cells twice.
    if (rh.view != null) return;
    // Per-row scoping IS the contract: a row without a resolvable local id
    // could only be emitted row-unscoped (`rows.*…`), so skip it instead.
    const localIds = this.#port.localIdsOf(rowId);
    if (localIds.length === 0) return;
    const first = fetched[0];
    if (first == null) return;
    const column = SemanticTime.dateKey(first.date);
    if (!column) return;
    const actor = this.#port.settleActor;
    rh.onNextFetchApplied = () => {
      // Breadcrumb: pairs 1:1 with `sheet/fetch-settle` (asserted by the
      // `fetch-settle-bind-pairing` session verification).
      Tracing.log("sheet/settle-invalidate", {
        rowKey: rowId,
        cells: fetched.length,
        column: column.toString(),
        rows: localIds.length,
      });
      // One event per rendered row sharing the key (same fan-out shape as
      // the rows-side canonical-cell fan-out) — almost always exactly one.
      for (const localId of localIds) {
        sink.emit(new ColumnDataChanged(actor, column, localId));
      }
    };
  }
}

export namespace RowFetchPlan {
  /**
   * Who owns a row's data, as the accessor resolves it from the row's
   * descriptor (`DataOwner`), with the row already applied.
   */
  export type Owner =
    /** A layout row: no cell data. */
    | { readonly kind: "layout" }
    /** A client-side provider: computes the row's cells for a day range. */
    | { readonly kind: "provider"; readonly computeCells: (range: NaiveDate.Range) => CellEntry[] }
    /** An alias: reads its source's batch key, its encoding inherited first. */
    | {
      readonly kind: "alias";
      readonly batchKey: string;
      readonly ensureEncoding: (rh: RowCellCache) => Promise<void>;
    }
    /** Any other row: fetched by its lookup key when it has one. */
    | { readonly kind: "plain"; readonly lookupKey: Option<string> };

  /** What the plan reads of its sheet, supplied by `CellAccessor`. */
  export interface Port {
    /** The sheets `Backend` the per-row reads by key go to. */
    readonly sheets: Pick<Backend, "fetchCellsByKey">;
    /** The client sink the settle announcements go to; `null` outside a sheet handle. */
    readonly invalidation: Option<InvalidationSink>;
    /** The actor a settle announcement carries (`CellAccessor.FETCH_SETTLE_ACTOR`). */
    readonly settleActor: Actor;
    /** The row's data owner, read at fetch time; `null` when the sheet has no metadata for it. */
    ownerOf(rowId: RowNetworkKey): Option<RowFetchPlan.Owner>;
    /** An alias's projected child rows, warmed when its bulk read delivered none. */
    childrenOf(rowId: RowNetworkKey): readonly RowNetworkKey[];
    /** The cache of a row, created on first use (`CellAccessor.rowHandle`). */
    rowHandle(rowId: RowNetworkKey): RowCellCache;
    /** The rendered rows sharing `rowId`'s key: a settle announcement goes to each. */
    localIdsOf(rowId: RowNetworkKey): readonly RowLocalId[];
  }
}
